/**
 * InlineCategorySelect — compact dropdown for choosing a GL account inline
 * on a transaction row. Used in both Banking (pending + posted) and AI Buck
 * queue so users can change the suggested category without expanding the
 * row first.
 *
 * Visual: shows the current category name + caret; opens a virtualized
 * search-friendly list of accounts. When the value is empty, shows
 * "Uncategorized" in italics (matches the existing Banking display).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  Popover, PopoverTrigger, PopoverContent,
} from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { Button } from './ui/button';

export default function InlineCategorySelect({
  value,
  onChange,
  accounts = [],
  placeholder = 'Uncategorized',
  disabled = false,
  size = 'sm',
  testId,
  buttonClassName,
  // When true, the trigger looks like a plain text label until hovered/clicked.
  // Banking uses this so the cell looks identical to its old read-only form
  // until the user reaches for it.
  ghost = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => accounts.find((a) => a.account_id === value),
    [accounts, value],
  );

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          variant={ghost ? 'ghost' : 'outline'}
          size={size}
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-7 justify-between gap-1 text-xs font-normal w-full max-w-[220px] truncate',
            ghost && !selected && 'text-muted-foreground italic',
            ghost && 'px-2 hover:bg-muted/60',
            buttonClassName,
          )}
          onClick={(e) => e.stopPropagation()}
          data-testid={testId || 'inline-category-select'}
        >
          <span className="truncate text-left">
            {selected ? (
              <span title={`${selected.code} · ${selected.name}`}>
                <span className="font-mono text-[10px] text-muted-foreground mr-1">
                  {selected.code}
                </span>
                {selected.name}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="h-3 w-3 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder="Search account…" className="h-9" />
          <CommandList className="max-h-72">
            <CommandEmpty>No accounts found.</CommandEmpty>
            <CommandGroup>
              {accounts.map((a) => (
                <CommandItem
                  key={a.account_id}
                  value={`${a.code} ${a.name} ${a.type || ''}`}
                  onSelect={() => {
                    onChange(a.account_id, a);
                    setOpen(false);
                  }}
                  data-testid={`inline-category-option-${a.account_id}`}
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      value === a.account_id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="font-mono text-[10px] text-muted-foreground mr-2">
                    {a.code}
                  </span>
                  <span className="truncate">{a.name}</span>
                  {a.type && (
                    <span className="ml-auto text-[10px] text-muted-foreground capitalize">
                      {a.type}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {/* Educational footer — explains the silent auto-rule
              behaviour. Helps the user understand WHY future
              transactions from the same vendor start posting
              automatically. */}
          <div
            className="border-t px-2.5 py-1.5 bg-muted/30 text-[10px] text-muted-foreground leading-tight"
            data-testid="inline-category-footer-hint"
          >
            Tip: categorize the same vendor 3+ times to auto-create a rule.{' '}
            <Link
              to="/ai-buck?tab=rules"
              className="text-emerald-600 dark:text-emerald-400 font-semibold hover:underline"
              data-testid="inline-category-rules-link"
            >
              View rules →
            </Link>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
